import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, ClipboardList, Search, Trash2, Edit3, X, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import * as XLSX from "xlsx";
import Pagination, { usePagination } from "@/components/Pagination";

const EMPTY_RC = {
  rc_no: "", rc_description: "", rc_date: "", rc_validity_date: "",
  rc_amount: 0, rc_company: "", project: "", active: true,
  c3_applied: false, c3_applied_date: "", c3_received_date: "",
  total_employee_in_c3: "", total_id_card_applied: "", total_employee_as_per_hrms: "",
  id_card_applied_status_update_date: "",
  total_id_card_approved: "", id_card_approved_status_update_date: "",
  noc_received: false, noc_received_date: "",
};

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function HRCompliance() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("hr_compliance", "create");
  const canEdit = hasPerm("hr_compliance", "edit");
  const canDelete = hasPerm("hr_compliance", "delete");

  const [opts, setOpts] = useState({ fy_list: [], current_fy: "", months: [] });
  const [fy, setFY] = useState("");
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [parties, setParties] = useState([]);
  const [q, setQ] = useState("");
  const [projectFilter, setProjectFilter] = useState("__all__");
  const [activeOnly, setActiveOnly] = useState(false);

  // Add/Edit dialog
  const [rcOpen, setRCOpen] = useState(false);
  const [rcForm, setRCForm] = useState(EMPTY_RC);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Detail (row click) dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRC, setDetailRC] = useState(null);
  const [monthForm, setMonthForm] = useState({});

  // Tabs
  const [tab, setTab] = useState("monthly"); // "monthly" | "manpower"

  useEffect(() => {
    api.get("/hr-compliance/options").then(({ data }) => {
      setOpts(data);
      setFY(data.current_fy);
    });
    api.get("/projects?active_only=true").then(({ data }) => setProjects(data || []));
    api.get("/parties").then(({ data }) => setParties(data || [])).catch(() => {});
  }, []);

  const load = async () => {
    if (!fy) return;
    const params = { fy };
    if (projectFilter !== "__all__") params.project = projectFilter;
    if (activeOnly) params.active_only = true;
    const { data } = await api.get("/hr-compliance/rcs", { params });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy, projectFilter, activeOnly]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((r) => {
      const hay = `${r.rc_no || ""} ${r.rc_description || ""} ${r.rc_company || ""} ${r.project || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q]);

  const {
    pageItems: pagedFiltered,
    page: hrPage, setPage: setHrPage,
    pageSize: hrPageSize, setPageSize: setHrPageSize,
    totalPages: hrTotalPages, totalItems: hrTotalItems,
  } = usePagination(filtered, "voltledger.hr.pageSize");

  // === Add / Edit ===
  const openAdd = () => {
    setEditingId(null);
    setRCForm(EMPTY_RC);
    setRCOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setRCForm({
      ...EMPTY_RC, ...r,
      rc_amount: r.rc_amount || 0,
      total_employee_in_c3: r.total_employee_in_c3 ?? "",
      total_id_card_applied: r.total_id_card_applied ?? "",
      total_employee_as_per_hrms: r.total_employee_as_per_hrms ?? "",
      total_id_card_approved: r.total_id_card_approved ?? "",
      c3_applied_date: r.c3_applied_date || "",
      c3_received_date: r.c3_received_date || "",
      id_card_applied_status_update_date: r.id_card_applied_status_update_date || "",
      id_card_approved_status_update_date: r.id_card_approved_status_update_date || "",
      noc_received_date: r.noc_received_date || "",
      rc_date: r.rc_date || "",
      rc_validity_date: r.rc_validity_date || "",
    });
    setRCOpen(true);
  };
  const saveRC = async () => {
    if (!rcForm.rc_no?.trim()) return toast.error("RC No is required");
    if (!rcForm.project) return toast.error("Project is required");
    setBusy(true);
    try {
      const payload = {
        ...rcForm,
        rc_amount: Number(rcForm.rc_amount || 0),
        total_employee_in_c3: rcForm.total_employee_in_c3 === "" ? null : Number(rcForm.total_employee_in_c3),
        total_id_card_applied: rcForm.total_id_card_applied === "" ? null : Number(rcForm.total_id_card_applied),
        total_employee_as_per_hrms: rcForm.total_employee_as_per_hrms === "" ? null : Number(rcForm.total_employee_as_per_hrms),
        total_id_card_approved: rcForm.total_id_card_approved === "" ? null : Number(rcForm.total_id_card_approved),
        rc_date: rcForm.rc_date || null,
        rc_validity_date: rcForm.rc_validity_date || null,
        c3_applied_date: rcForm.c3_applied_date || null,
        c3_received_date: rcForm.c3_received_date || null,
        id_card_applied_status_update_date: rcForm.id_card_applied_status_update_date || null,
        id_card_approved_status_update_date: rcForm.id_card_approved_status_update_date || null,
        noc_received_date: rcForm.noc_received_date || null,
      };
      if (editingId) {
        await api.put(`/hr-compliance/rcs/${editingId}`, payload);
        toast.success("RC updated");
      } else {
        await api.post("/hr-compliance/rcs", payload);
        toast.success("RC created");
      }
      setRCOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const removeRC = async (r) => {
    if (!window.confirm(`Delete RC '${r.rc_no}'? This cannot be undone.`)) return;
    try {
      await api.delete(`/hr-compliance/rcs/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // === Monthly grid (detail dialog) ===
  const openDetail = (r) => {
    setDetailRC(r);
    setMonthForm({ ...(r.fy_months || {}) });
    setDetailOpen(true);
  };
  const updateMonth = (m, patch) => {
    setMonthForm((prev) => ({ ...prev, [m]: { ...prev[m], ...patch } }));
  };
  const saveMonth = async (m) => {
    if (!detailRC) return;
    try {
      const status = monthForm[m] || {};
      const noWork = !!status.no_work;
      const payload = {
        compliance_applied: noWork ? false : !!status.compliance_applied,
        compliance_applied_date: noWork ? null : (status.compliance_applied_date || null),
        compliance_id: noWork ? "" : (status.compliance_id || ""),
        approval_status: noWork ? false : !!status.approval_status,
        approval_date: noWork ? null : (status.approval_date || null),
        no_work: noWork,
      };
      await api.put(`/hr-compliance/rcs/${detailRC.id}/months/${fy}/${m}`, payload);
      toast.success(`${m} ${fy} saved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const cellTone = (st) => {
    if (st?.no_work) return "bg-rose-50 border-rose-300 text-rose-700";
    if (st?.approval_status) return "bg-emerald-50 border-emerald-300 text-emerald-700";
    if (st?.compliance_applied) return "bg-amber-50 border-amber-300 text-amber-700";
    return "bg-slate-50 border-slate-200 text-slate-400";
  };
  const cellLabel = (st) => {
    if (st?.no_work) return "No Work";
    if (st?.approval_status) return "Approved";
    if (st?.compliance_applied) return "Applied";
    return "—";
  };

  // Totals across the visible RCs
  const totals = useMemo(() => {
    let applied = 0, approved = 0, noWork = 0;
    filtered.forEach((r) => {
      Object.values(r.fy_months || {}).forEach((st) => {
        if (st.no_work) noWork++;
        else {
          if (st.compliance_applied) applied++;
          if (st.approval_status) approved++;
        }
      });
    });
    return { applied, approved, noWork, total: filtered.length * (opts.months?.length || 12) };
  }, [filtered, opts.months]);

  // === Add a new FY to the dropdown (purely client-side; data is created lazily on first month save) ===
  const addNewFY = () => {
    const input = window.prompt(
      "Enter Financial Year in YYYY-YY format (e.g. 2028-29).\n\nThe FY will be added to the dropdown and selected. " +
      "It will persist permanently once you save any monthly status against it.",
      ""
    );
    if (!input) return;
    const v = String(input).trim();
    // Accept YYYY-YY where YY = (YYYY+1) mod 100
    const m = v.match(/^(\d{4})-(\d{2})$/);
    if (!m) return toast.error("Invalid format. Use YYYY-YY (e.g. 2028-29).");
    const y1 = parseInt(m[1], 10);
    const y2 = parseInt(m[2], 10);
    const expected = (y1 + 1) % 100;
    if (y2 !== expected) return toast.error(`Invalid range. After ${y1} the next year ends in ${String(expected).padStart(2, "0")}.`);
    if ((opts.fy_list || []).includes(v)) {
      setFY(v);
      return toast.info(`${v} already exists — switched to it.`);
    }
    const newList = [...(opts.fy_list || []), v].sort().reverse();
    setOpts({ ...opts, fy_list: newList });
    setFY(v);
    toast.success(`FY ${v} added`);
  };

  // === Export to Excel — both tabs as separate sheets ===
  const exportExcel = () => {
    if (!filtered.length) {
      toast.error("No data to export");
      return;
    }
    const months = opts.months || [];
    const wb = XLSX.utils.book_new();

    // --- Sheet 1: Monthly Compliance Status (RC info + 12 × 5 month cells) ---
    const monthlyHeader = [
      "Sl No", "RC No", "Description", "Project", "Client", "Active",
      "RC Date", "RC Validity", "RC Amount",
      "C3 Applied", "C3 Applied Date", "C3 Received Date",
      "Employees in C3", "Employees per HRMS",
      "ID Cards Applied", "ID Card Applied Status Date",
      "ID Cards Approved", "ID Card Approved Status Date",
      "NOC Received", "NOC Received Date",
    ];
    months.forEach((m) => {
      monthlyHeader.push(
        `${m} - Status`,
        `${m} - Applied`, `${m} - Applied Date`, `${m} - Compliance ID`,
        `${m} - Approved`, `${m} - Approval Date`,
      );
    });
    const monthlyRows = [monthlyHeader];
    filtered.forEach((r, idx) => {
      const row = [
        idx + 1, r.rc_no, r.rc_description || "", r.project, r.rc_company || "",
        r.active ? "Yes" : "No",
        r.rc_date || "", r.rc_validity_date || "", Number(r.rc_amount || 0),
        r.c3_applied ? "Yes" : "No", r.c3_applied_date || "", r.c3_received_date || "",
        r.total_employee_in_c3 ?? "", r.total_employee_as_per_hrms ?? "",
        r.total_id_card_applied ?? "", r.id_card_applied_status_update_date || "",
        r.total_id_card_approved ?? "", r.id_card_approved_status_update_date || "",
        r.noc_received ? "Yes" : "No", r.noc_received_date || "",
      ];
      months.forEach((m) => {
        const st = r.fy_months?.[m] || {};
        const label = st.no_work ? "No Work" : st.approval_status ? "Approved" : st.compliance_applied ? "Applied" : "Pending";
        row.push(
          label,
          st.no_work ? "" : (st.compliance_applied ? "Yes" : "No"),
          st.no_work ? "" : (st.compliance_applied_date || ""),
          st.no_work ? "" : (st.compliance_id || ""),
          st.no_work ? "" : (st.approval_status ? "Yes" : "No"),
          st.no_work ? "" : (st.approval_date || ""),
        );
      });
      monthlyRows.push(row);
    });
    const ws1 = XLSX.utils.aoa_to_sheet(monthlyRows);
    ws1["!cols"] = [
      { wch: 6 }, { wch: 14 }, { wch: 32 }, { wch: 18 }, { wch: 14 }, { wch: 10 },
      { wch: 12 }, { wch: 12 }, { wch: 14 },
      { wch: 11 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 16 },
      { wch: 16 }, { wch: 20 },
      { wch: 16 }, { wch: 20 },
      { wch: 13 }, { wch: 14 },
      ...months.flatMap(() => [{ wch: 11 }, { wch: 14 }, { wch: 16 }, { wch: 11 }, { wch: 14 }]),
    ];
    // Bold header row
    for (let c = 0; c < monthlyHeader.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (ws1[addr]) ws1[addr].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, ws1, `Compliance FY ${fy}`);

    // --- Sheet 2: Manpower & ID Card Summary ---
    const manpowerHeader = [
      "Sl No", "RC No", "RC Description (Department Name)", "RC Company",
      "Project Name", "Employees per HRMS", "ID Cards Applied", "ID Cards Approved",
    ];
    const manpowerRows = [manpowerHeader];
    let sumHrms = 0, sumApplied = 0, sumApproved = 0;
    filtered.forEach((r, idx) => {
      const hrms = Number(r.total_employee_as_per_hrms || 0);
      const applied = Number(r.total_id_card_applied || 0);
      const approved = Number(r.total_id_card_approved || 0);
      sumHrms += hrms; sumApplied += applied; sumApproved += approved;
      manpowerRows.push([
        idx + 1, r.rc_no, r.rc_description || "", r.rc_company || "",
        r.project,
        r.total_employee_as_per_hrms ?? "",
        r.total_id_card_applied ?? "",
        r.total_id_card_approved ?? "",
      ]);
    });
    manpowerRows.push([
      "", "", "", "", `Total · ${filtered.length} RCs`,
      sumHrms, sumApplied, sumApproved,
    ]);
    const ws2 = XLSX.utils.aoa_to_sheet(manpowerRows);
    ws2["!cols"] = [
      { wch: 6 }, { wch: 16 }, { wch: 38 }, { wch: 16 },
      { wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 18 },
    ];
    for (let c = 0; c < manpowerHeader.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (ws2[addr]) ws2[addr].s = { font: { bold: true } };
    }
    // Bold totals row
    const totalRowIdx = manpowerRows.length - 1;
    for (let c = 0; c < manpowerHeader.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
      if (ws2[addr]) ws2[addr].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, ws2, "Manpower & ID Cards");

    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    XLSX.writeFile(wb, `HR_Compliance_FY${fy}_${ts}.xlsx`);
    toast.success(`Exported ${filtered.length} RC(s) · 2 sheets`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1800px]">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Compliance</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">HR Compliance Status</h1>
          <p className="text-sm text-slate-500 mt-2">
            Track Rate Contract–level compliance applied vs approved month by month for the selected financial year.
            {fy && <span className="ml-2 text-indigo-700 font-semibold">· FY {fy}</span>}
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Financial Year</Label>
            <div className="flex items-center gap-1">
              <Select value={fy} onValueChange={setFY}>
                <SelectTrigger className="rounded-none h-10 w-36" data-testid="hr-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opts.fy_list?.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <button
                onClick={addNewFY}
                title="Add a new Financial Year"
                aria-label="Add new FY"
                className="h-10 w-10 inline-flex items-center justify-center border border-slate-300 hover:border-[#002FA7] hover:text-[#002FA7] text-slate-600 transition-colors"
                data-testid="hr-fy-add"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <Button onClick={exportExcel} variant="outline" className="rounded-none h-10 border-slate-300" data-testid="hr-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
          {canCreate && (
            <Button onClick={openAdd} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10" data-testid="hr-add-rc-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Rate Contract
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-slate-200 mb-4 gap-1" data-testid="hr-tabs">
        <button
          onClick={() => setTab("monthly")}
          data-testid="hr-tab-monthly"
          className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === "monthly" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          Monthly Compliance Status
        </button>
        <button
          onClick={() => setTab("manpower")}
          data-testid="hr-tab-manpower"
          className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === "manpower" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          Manpower &amp; ID Card Summary
        </button>
      </div>

      {/* Filter row */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-3 flex items-center flex-wrap gap-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filters</div>
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Search RC #, description, client, project…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none pl-9 h-9 text-sm border-slate-300"
            data-testid="hr-search"
          />
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="rounded-none h-9 w-56" data-testid="hr-filter-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700">
          <Switch checked={activeOnly} onCheckedChange={setActiveOnly} data-testid="hr-active-only" />
          <span>Active RCs only</span>
        </label>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-600">
          <span><strong className="text-slate-900">{filtered.length}</strong> RCs</span>
          <span className="text-amber-700"><strong>{totals.applied}</strong> applied</span>
          <span className="text-emerald-700"><strong>{totals.approved}</strong> approved</span>
          {totals.noWork > 0 && <span className="text-rose-700"><strong>{totals.noWork}</strong> no-work</span>}
        </div>
      </div>

      {/* Table — Monthly Compliance */}
      {tab === "monthly" && (
      <>
      <div className="bg-white border border-slate-200" data-testid="hr-table">
        <table className="w-full text-sm border-collapse table-fixed">
          <thead>
            <tr className="bg-gradient-to-r from-sky-100 via-blue-100 to-indigo-100 text-slate-800 border-b-2 border-indigo-300">
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "120px" }}>RC No</th>
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "180px" }}>Description</th>
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>Project</th>
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "100px" }}>Client</th>
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "80px" }}>Active</th>
              <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider">Monthly Compliance Status (FY {fy})</th>
              <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "90px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-400">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No rate contracts found{q || projectFilter !== "__all__" || activeOnly ? " for the current filters." : ". Add one to get started."}
              </td></tr>
            )}
            {pagedFiltered.map((r, idx) => (
              <tr key={r.id} className={`border-b border-slate-200 hover:bg-sky-50 cursor-pointer align-top transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-sky-50/30"}`} data-testid={`hr-row-${r.rc_no}`} onClick={() => openDetail(r)}>
                <td className="px-3 py-3 font-mono-data font-semibold text-slate-900 break-words">{r.rc_no}</td>
                <td className="px-3 py-3 text-slate-700 break-words">{r.rc_description || "—"}</td>
                <td className="px-3 py-3 text-slate-700 break-words">{r.project}</td>
                <td className="px-3 py-3 text-slate-600 break-words">{r.rc_company || "—"}</td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded border ${r.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="grid grid-cols-6 gap-1">
                    {opts.months?.map((m) => {
                      const st = r.fy_months?.[m] || {};
                      return (
                        <div key={m} className={`border rounded px-1 py-1 text-center ${cellTone(st)}`}>
                          <div className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{m}</div>
                          <div className="text-[10px] font-bold mt-0.5">{cellLabel(st)}</div>
                        </div>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {canEdit && (
                    <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="text-slate-500 hover:text-[#002FA7] p-1" title="Edit RC details" data-testid={`hr-edit-${r.rc_no}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={(e) => { e.stopPropagation(); removeRC(r); }} className="text-slate-500 hover:text-rose-700 p-1 ml-1" title="Delete RC" data-testid={`hr-del-${r.rc_no}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500 mt-3">
        <span className="inline-block w-3 h-3 bg-amber-50 border border-amber-300 align-middle mr-1"></span> Applied
        <span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-300 align-middle ml-3 mr-1"></span> Approved
        <span className="inline-block w-3 h-3 bg-slate-50 border border-slate-200 align-middle ml-3 mr-1"></span> No status yet
        — click any row to update monthly status.
      </p>
      <Pagination
        page={hrPage} setPage={setHrPage}
        pageSize={hrPageSize} setPageSize={setHrPageSize}
        totalPages={hrTotalPages} totalItems={hrTotalItems}
        testId="hr-monthly-pagination"
      />
      </>
      )}

      {/* Manpower & ID Card Summary */}
      {tab === "manpower" && (
        <div className="bg-white border border-slate-200" data-testid="hr-manpower-table">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-amber-100 via-yellow-200 to-amber-100 text-slate-900 border-b-2 border-amber-400">
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "60px" }}>Sl No</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>RC No</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider">RC Description (Department Name)</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>RC Company</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "160px" }}>Project Name</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>Employees per HRMS</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>ID Cards Applied</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "150px" }}>ID Cards Approved</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-sm text-slate-400">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No rate contracts found{q || projectFilter !== "__all__" || activeOnly ? " for the current filters." : "."}
                </td></tr>
              )}
              {pagedFiltered.map((r, idx) => {
                const hrms = r.total_employee_as_per_hrms;
                const applied = r.total_id_card_applied;
                const approved = r.total_id_card_approved;
                const apprToneClass = (approved != null && applied != null && approved < applied) ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold";
                return (
                  <tr key={r.id} className={`border-b border-slate-200 hover:bg-amber-50 cursor-pointer transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-amber-50/40"}`} data-testid={`hr-mp-row-${r.rc_no}`} onClick={() => openEdit(r)}>
                    <td className="px-3 py-2.5 text-slate-500 font-mono-data">{String(idx + 1).padStart(2, "0")}</td>
                    <td className="px-3 py-2.5 font-mono-data font-semibold text-slate-900">{r.rc_no}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.rc_description || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.rc_company || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.project}</td>
                    <td className="px-3 py-2.5 text-right font-mono-data text-slate-900">{hrms != null ? hrms : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono-data text-slate-900">{applied != null ? applied : "—"}</td>
                    <td className={`px-3 py-2.5 text-right font-mono-data ${approved != null ? apprToneClass : "text-slate-400"}`}>{approved != null ? approved : "—"}</td>
                  </tr>
                );
              })}
              {filtered.length > 0 && (
                <tr className="bg-gradient-to-r from-amber-50 via-yellow-100 to-amber-50 border-t-2 border-amber-400" data-testid="hr-mp-totals">
                  <td colSpan={5} className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.2em] text-amber-900">Total · {filtered.length} RCs</td>
                  <td className="px-3 py-2.5 text-right font-mono-data font-bold text-slate-900">
                    {filtered.reduce((s, r) => s + (Number(r.total_employee_as_per_hrms) || 0), 0)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono-data font-bold text-slate-900">
                    {filtered.reduce((s, r) => s + (Number(r.total_id_card_applied) || 0), 0)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono-data font-bold text-emerald-700">
                    {filtered.reduce((s, r) => s + (Number(r.total_id_card_approved) || 0), 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {tab === "manpower" && (
        <>
          <p className="text-[11px] text-slate-500 mt-3">Click any row to edit the RC and update employee/ID-card counts.</p>
          <Pagination
            page={hrPage} setPage={setHrPage}
            pageSize={hrPageSize} setPageSize={setHrPageSize}
            totalPages={hrTotalPages} totalItems={hrTotalItems}
            testId="hr-pagination"
          />
        </>
      )}

      {/* Add/Edit RC dialog */}
      <Dialog open={rcOpen} onOpenChange={setRCOpen}>
        <DialogContent className="max-w-4xl rounded-none">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Rate Contract" : "Add Rate Contract"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 max-h-[70vh] overflow-y-auto pr-2">
            <Field label="RC No *">
              <Input value={rcForm.rc_no} onChange={(e) => setRCForm({ ...rcForm, rc_no: e.target.value })} className="rounded-none" data-testid="hr-form-rc-no" />
            </Field>
            <Field label="RC Description (Department)">
              <Input value={rcForm.rc_description} onChange={(e) => setRCForm({ ...rcForm, rc_description: e.target.value })} className="rounded-none" />
            </Field>
            <Field label="RC Date">
              <Input type="date" value={rcForm.rc_date || ""} onChange={(e) => setRCForm({ ...rcForm, rc_date: e.target.value })} className="rounded-none" />
            </Field>
            <Field label="RC Validity Date">
              <Input type="date" value={rcForm.rc_validity_date || ""} onChange={(e) => setRCForm({ ...rcForm, rc_validity_date: e.target.value })} className="rounded-none" />
            </Field>
            <Field label="RC Amount (₹)">
              <Input type="number" value={rcForm.rc_amount} onChange={(e) => setRCForm({ ...rcForm, rc_amount: e.target.value })} className="rounded-none font-mono-data" />
              {rcForm.rc_amount > 0 && <div className="text-[11px] text-slate-500 mt-1">{fmtINR(rcForm.rc_amount)}</div>}
            </Field>
            <Field label="Project *">
              <Select value={rcForm.project} onValueChange={(v) => setRCForm({ ...rcForm, project: v })}>
                <SelectTrigger className="rounded-none" data-testid="hr-form-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Client / RC Company">
              <Select value={rcForm.rc_company || "__none__"} onValueChange={(v) => setRCForm({ ...rcForm, rc_company: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {parties.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <label className="inline-flex items-center gap-2 mt-2">
                <Switch checked={rcForm.active} onCheckedChange={(v) => setRCForm({ ...rcForm, active: v })} />
                <span className="text-sm">{rcForm.active ? "Active" : "Inactive"}</span>
              </label>
            </Field>

            <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">C3 / ID Card / NOC</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="C3 Applied">
                  <label className="inline-flex items-center gap-2 mt-2">
                    <Switch checked={rcForm.c3_applied} onCheckedChange={(v) => setRCForm({ ...rcForm, c3_applied: v })} />
                    <span className="text-sm">{rcForm.c3_applied ? "Yes" : "No"}</span>
                  </label>
                </Field>
                <Field label="C3 Applied Date">
                  <Input type="date" value={rcForm.c3_applied_date || ""} onChange={(e) => setRCForm({ ...rcForm, c3_applied_date: e.target.value })} className="rounded-none" disabled={!rcForm.c3_applied} />
                </Field>
                <Field label="C3 Received Date">
                  <Input type="date" value={rcForm.c3_received_date || ""} onChange={(e) => setRCForm({ ...rcForm, c3_received_date: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="Employees in C3">
                  <Input type="number" value={rcForm.total_employee_in_c3} onChange={(e) => setRCForm({ ...rcForm, total_employee_in_c3: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="ID Cards Applied">
                  <Input type="number" value={rcForm.total_id_card_applied} onChange={(e) => setRCForm({ ...rcForm, total_id_card_applied: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="Employees per HRMS">
                  <Input type="number" value={rcForm.total_employee_as_per_hrms} onChange={(e) => setRCForm({ ...rcForm, total_employee_as_per_hrms: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="ID Card Applied — Status Update Date">
                  <Input type="date" value={rcForm.id_card_applied_status_update_date || ""} onChange={(e) => setRCForm({ ...rcForm, id_card_applied_status_update_date: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="ID Cards Approved">
                  <Input type="number" value={rcForm.total_id_card_approved} onChange={(e) => setRCForm({ ...rcForm, total_id_card_approved: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="ID Card Approved — Status Update Date">
                  <Input type="date" value={rcForm.id_card_approved_status_update_date || ""} onChange={(e) => setRCForm({ ...rcForm, id_card_approved_status_update_date: e.target.value })} className="rounded-none" />
                </Field>
                <Field label="NOC Received (against inactive RC)">
                  <label className="inline-flex items-center gap-2 mt-2">
                    <Switch checked={rcForm.noc_received} onCheckedChange={(v) => setRCForm({ ...rcForm, noc_received: v })} />
                    <span className="text-sm">{rcForm.noc_received ? "Yes" : "No"}</span>
                  </label>
                </Field>
                <Field label="NOC Received Date">
                  <Input type="date" value={rcForm.noc_received_date || ""} onChange={(e) => setRCForm({ ...rcForm, noc_received_date: e.target.value })} className="rounded-none" disabled={!rcForm.noc_received} />
                </Field>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRCOpen(false)} className="rounded-none"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={saveRC} disabled={busy} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="hr-form-save">
              <Save className="w-4 h-4 mr-1.5" /> {editingId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail (Monthly grid) dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-5xl rounded-none">
          <DialogHeader>
            <DialogTitle>
              {detailRC?.rc_no} · <span className="text-slate-500 font-normal">{detailRC?.rc_description || "—"}</span>
              <span className="ml-3 text-xs font-normal text-indigo-700">FY {fy}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-1 max-h-[72vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-slate-50 border border-slate-200 px-3 py-2">
              <KV k="Project" v={detailRC?.project} />
              <KV k="Client" v={detailRC?.rc_company || "—"} />
              <KV k="Active" v={detailRC?.active ? "Yes" : "No"} />
              <KV k="RC Amount" v={fmtINR(detailRC?.rc_amount || 0)} />
              <KV k="C3 Applied" v={detailRC?.c3_applied ? `Yes · ${detailRC?.c3_applied_date || ""}` : "No"} />
              <KV k="C3 Received" v={detailRC?.c3_received_date || "—"} />
              <KV k="Employees in C3 / HRMS" v={`${detailRC?.total_employee_in_c3 ?? "—"} / ${detailRC?.total_employee_as_per_hrms ?? "—"}`} />
              <KV k="ID Cards Applied / Approved" v={`${detailRC?.total_id_card_applied ?? "—"} / ${detailRC?.total_id_card_approved ?? "—"}`} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Monthly Compliance Status — FY {fy}</div>
              <div className="overflow-x-auto border border-slate-200">
                <table className="w-full text-sm" style={{ minWidth: "980px" }}>
                  <thead className="bg-slate-100">
                    <tr className="border-b border-slate-300">
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "70px" }}>Month</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50/60" style={{ width: "80px" }}>No Work</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "80px" }}>Applied?</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "170px" }}>Applied Date</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "170px" }}>Compliance ID</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "80px" }}>Approved?</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "170px" }}>Approval Date</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-700" style={{ width: "70px" }}>Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opts.months?.map((m) => {
                      const st = monthForm[m] || {};
                      const isNoWork = !!st.no_work;
                      return (
                        <tr key={m} className={`border-b border-slate-100 ${isNoWork ? "bg-rose-50/40" : ""}`} data-testid={`hr-detail-${m}`}>
                          <td className="px-3 py-2 font-semibold text-slate-900">{m}</td>
                          <td className="px-3 py-2 text-center bg-rose-50/30">
                            <Switch
                              checked={isNoWork}
                              onCheckedChange={(v) => updateMonth(m, v ? { no_work: true, compliance_applied: false, compliance_applied_date: "", compliance_id: "", approval_status: false, approval_date: "" } : { no_work: false })}
                              disabled={!canEdit}
                              data-testid={`hr-no-work-${m}`}
                              className="data-[state=checked]:bg-rose-600"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Switch checked={!!st.compliance_applied} onCheckedChange={(v) => updateMonth(m, { compliance_applied: v })} disabled={!canEdit || isNoWork} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="date" value={st.compliance_applied_date || ""} onChange={(e) => updateMonth(m, { compliance_applied_date: e.target.value })} disabled={!canEdit || !st.compliance_applied || isNoWork} className="rounded-none h-9 text-xs w-full" />
                          </td>
                          <td className="px-2 py-2">
                            <Input value={st.compliance_id || ""} onChange={(e) => updateMonth(m, { compliance_id: e.target.value })} placeholder="CMP-..." disabled={!canEdit || !st.compliance_applied || isNoWork} className="rounded-none h-9 text-xs w-full" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Switch checked={!!st.approval_status} onCheckedChange={(v) => updateMonth(m, { approval_status: v })} disabled={!canEdit || !st.compliance_applied || isNoWork} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="date" value={st.approval_date || ""} onChange={(e) => updateMonth(m, { approval_date: e.target.value })} disabled={!canEdit || !st.approval_status || isNoWork} className="rounded-none h-9 text-xs w-full" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button onClick={() => saveMonth(m)} disabled={!canEdit} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-none h-8 px-2 text-xs" data-testid={`hr-save-${m}`}>
                              <Save className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Tip: Toggle <span className="font-semibold text-rose-700">No Work</span> when no manpower was deployed for that month — compliance becomes not-applicable and the row is excluded from the approval-rate denominator. Toggle Applied → Approved otherwise.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-none">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

const KV = ({ k, v }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className="font-medium text-slate-800">{v}</div>
  </div>
);
